/**
 * Favoris et vues sauvegardées.
 * Stockage local (anonyme) par défaut ; si l'utilisateur est connecté, synchronisation avec la
 * table `user_favorites` pour retrouver les mêmes favoris sur plusieurs appareils.
 */
import { useSyncExternalStore } from "react";
import { supabase } from "./supabase";

export type FavoriteKind = "team" | "player" | "comparison" | "stats_view";
export interface Favorite {
  kind: FavoriteKind;
  ref: string;
  label: string;
  payload?: unknown;
  created_at: string;
}

const KEY = "ha-favorites-v1";
let cache: Favorite[] | null = null;
const listeners = new Set<() => void>();

function read(): Favorite[] {
  if (cache) return cache;
  try {
    const raw = localStorage.getItem(KEY);
    cache = raw ? (JSON.parse(raw) as Favorite[]) : [];
  } catch {
    cache = [];
  }
  return cache;
}

function write(next: Favorite[]) {
  cache = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* stockage indisponible : les favoris restent en mémoire pour la session */
  }
  listeners.forEach((l) => l());
}

async function cloudUserId(): Promise<string | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session?.user.id ?? null;
}

export function isFavorite(kind: FavoriteKind, ref: string): boolean {
  return read().some((f) => f.kind === kind && f.ref === ref);
}

export async function toggleFavorite(kind: FavoriteKind, ref: string, label: string, payload?: unknown) {
  const list = read();
  const exists = list.some((f) => f.kind === kind && f.ref === ref);
  const next = exists
    ? list.filter((f) => !(f.kind === kind && f.ref === ref))
    : [...list, { kind, ref, label, payload, created_at: new Date().toISOString() }];
  write(next);
  const uid = await cloudUserId();
  if (uid && supabase) {
    if (exists) await supabase.from("user_favorites").delete().match({ user_id: uid, kind, ref });
    else await supabase.from("user_favorites").upsert({ user_id: uid, kind, ref, label, payload: payload ?? null }, { onConflict: "user_id,kind,ref" });
  }
}

/** Fusionne favoris locaux et distants après connexion. */
export async function syncFavoritesWithCloud() {
  const uid = await cloudUserId();
  if (!uid || !supabase) return;
  const { data } = await supabase.from("user_favorites").select("kind, ref, label, payload, created_at");
  const remote = (data ?? []) as Favorite[];
  const local = read();
  const merged = [...remote];
  for (const f of local) if (!merged.some((m) => m.kind === f.kind && m.ref === f.ref)) merged.push(f);
  write(merged);
  const missingRemote = local.filter((f) => !remote.some((r) => r.kind === f.kind && r.ref === f.ref));
  if (missingRemote.length) {
    await supabase.from("user_favorites").upsert(
      missingRemote.map((f) => ({ user_id: uid, kind: f.kind, ref: f.ref, label: f.label, payload: f.payload ?? null })),
      { onConflict: "user_id,kind,ref" },
    );
  }
}

function subscribe(l: () => void) {
  listeners.add(l);
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY) {
      cache = null;
      l();
    }
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(l);
    window.removeEventListener("storage", onStorage);
  };
}

export function useFavorites(kind?: FavoriteKind): Favorite[] {
  const all = useSyncExternalStore(subscribe, read, () => [] as Favorite[]);
  return kind ? all.filter((f) => f.kind === kind) : all;
}
